class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    // AF: a tick or a choice in a select is a decision, so the list is fetched at once. Only a
    // typed field (the price range) waits half a second for the typing to stop.
    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', (event) => {
      if (event.target.matches('input[type="checkbox"], input[type="radio"], select')) this.onSubmitHandler(event);
      else this.debouncedOnSubmit(event);
    });

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll('.facets-container .loading__spinner, facet-filters-form .loading__spinner');
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;
      // AF: choices are no longer held back, so two requests can be on their way; the later choice wins
      FacetFiltersForm.latestUrl = url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        if (FacetFiltersForm.latestUrl !== url) return; // an answer to an earlier choice: kept for Back, not shown
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });

    // AF: the grid was replaced (assets/af-collection.js re-applies the density image sizes)
    document.dispatchEvent(new CustomEvent('af:grid-updated'));
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll('.facets-container .loading__spinner, facet-filters-form .loading__spinner');
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    const facetDetailsElements = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    };
    const facetsToRender = Array.from(facetDetailsElements).filter((element) => !matchesIndex(element));
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      document.querySelector(`.js-filter[data-index="${element.dataset.index}"]`).innerHTML = element.innerHTML;
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML, event);
    FacetFiltersForm.restoreRemoveFocus();

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newElementSelector = document
          .getElementById(closestJSFilterID)
          .classList.contains('mobile-facets__details')
          ? `#${closestJSFilterID} .mobile-facets__close-button`
          : `#${closestJSFilterID} .facets__summary`;
        const newElementToActivate = document.querySelector(newElementSelector);
        // AF: focus stays on the control that was used (the price field being typed in, or the new
        // copy of the ticked option); it used to jump to the filter's first row after every answer,
        // which also took the rest of a typed price away from its field
        const sameControl = event.target.id ? document.getElementById(event.target.id) : null;
        if (sameControl && sameControl.getClientRects().length) {
          if (document.activeElement !== sameControl) sameControl.focus({ preventScroll: true });
        } else if (newElementToActivate) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  // AF: a chip, "Remove all" or a "Clear" link is replaced by the answer to its own click, and
  // keyboard focus fell to the page. onActiveFilterClick() notes where the link was; once the new
  // rows are in, focus goes to the chip now in that place (the last one if it was the last), to
  // the same "Clear" link in the drawer, or to the Filter button when no chip is left. Nothing
  // moves if focus is somewhere else by then (a pointer user who has gone on to another control).
  static restoreRemoveFocus() {
    const from = FacetFiltersForm.removeFocus;
    if (!from) return;
    FacetFiltersForm.removeFocus = null;
    if (document.activeElement && document.activeElement !== document.body) return;
    const shown = (element) => element && element.getClientRects().length > 0;
    let target = null;
    if (from.row) {
      const row = document.querySelector(from.row);
      const chips = row ? Array.from(row.querySelectorAll('facet-remove:not(.active-facets__button-wrapper) a')).filter(shown) : [];
      if (from.index >= 0 && chips.length) target = chips[Math.min(from.index, chips.length - 1)];
    } else if (from.filter) {
      const filter = document.getElementById(from.filter);
      target = filter && filter.querySelector('.mobile-facets__footer .mobile-facets__clear');
    } else if (from.drawer) {
      target = document.querySelector('.mobile-facets__main > .mobile-facets__footer .mobile-facets__clear');
    }
    if (!shown(target)) {
      const open = document.querySelector('.mobile-facets__disclosure.menu-opening[open] .mobile-facets__inner');
      target = open || Array.from(document.querySelectorAll('summary.mobile-facets__open-wrapper')).find(shown);
    }
    if (target) target.focus({ preventScroll: true });
  }

  static renderAdditionalElements(html, event) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];
    // AF: the control the shopper has just used is not replaced under her (the toolbar's sort
    // select used to be swapped for a copy of itself, and keyboard focus fell to the page)
    const source = event && event.target instanceof Element ? event.target : null;
    const focusId = source && source.id;

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      const target = document.querySelector(selector);
      if (!target || (source && target.contains(source))) return;
      target.innerHTML = html.querySelector(selector).innerHTML;
    });

    // If the control was replaced after all, focus goes to its new copy
    if (focusId && (!document.activeElement || document.activeElement === document.body)) {
      const again = document.getElementById(focusId);
      if (again) again.focus({ preventScroll: true });
    }

    // AF: the drawer's apply buttons read "Show N items"; keep N in step with the results
    const applyLabel = html.querySelector('[data-af-apply-label]');
    if (applyLabel) {
      document.querySelectorAll('[data-af-apply-label]').forEach((button) => {
        button.textContent = applyLabel.textContent;
      });
    }

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            const noJsElements = document.querySelectorAll('.no-js-list');
            noJsElements.forEach((el) => el.remove());
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      // AF: with the drawer layout the filters are in the drawer's form only, so a sort chosen in the
      // toolbar (tablet and desktop) used to send its own form alone and the filters were dropped.
      // The drawer's choices go with it, without the drawer's own sort and the search fields both carry.
      const drawerForm = document.getElementById('FacetFiltersFormMobile');
      if (!isMobile && drawerForm && !document.getElementById('FacetFiltersForm')) {
        const kept = new URLSearchParams(new FormData(drawerForm));
        ['sort_by', 'q', 'options[prefix]'].forEach((name) => kept.delete(name));
        if (kept.toString()) forms.push(kept.toString());
      }
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    // AF: where the link is, for restoreRemoveFocus() once the rows have been replaced
    const link = event.currentTarget;
    const row = link.closest('.active-facets-mobile, .active-facets-desktop');
    if (row) {
      const chips = Array.from(row.querySelectorAll('facet-remove:not(.active-facets__button-wrapper) a')).filter(
        (element) => element.getClientRects().length > 0
      );
      FacetFiltersForm.removeFocus = {
        row: row.classList.contains('active-facets-mobile') ? '.active-facets-mobile' : '.active-facets-desktop',
        index: chips.indexOf(link),
      };
    } else if (link.closest('.mobile-facets__inner')) {
      const filter = link.closest('.js-filter');
      FacetFiltersForm.removeFocus = filter && filter.id ? { filter: filter.id } : { drawer: true };
    } else {
      FacetFiltersForm.removeFocus = null;
    }
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) =>
      element.addEventListener('change', this.onRangeChange.bind(this))
    );
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('min', 0);
    if (maxInput.value === '') minInput.setAttribute('max', maxInput.getAttribute('max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);

/*
  AFTER FIVE: the filter drawer is a dialog (WCAG 2.4.3, 4.1.2).
  The theme's <menu-drawer> opens it with keyboard focus left on the "Filter and sort" trigger,
  which lies behind the drawer on a phone, and keeps that trigger in the Tab cycle. Here focus
  moves into the dialog when it opens (the dialog is named, so it is announced), and Tab stays
  among the controls that can be seen in it: the header's close button, then the list of filters
  or the one filter that is open. Escape and closing are the theme's: both return focus to the
  trigger.
*/
(function () {
  function openDrawer() {
    // "menu-opening" goes the moment the drawer starts to close; [open] stays until it has slid out
    return document.querySelector('.mobile-facets__disclosure.menu-opening[open]');
  }

  // The trigger sits in the toolbar, which sticks to the top of the screen. On opening, the theme
  // focuses the trigger (trapFocus in global.js) before focus moves into the dialog. Where the
  // click itself has not focused it (Safari does not focus a control that is tapped or clicked;
  // a click sent by a script), that focus made the browser "reveal" the trigger from under the
  // page's scroll-padding-top and a scrolled list moved up some 430px behind the drawer. The
  // trigger is on screen: it takes focus here first, without scrolling, and the theme's call
  // then changes nothing.
  document.addEventListener(
    'click',
    function (event) {
      const summary = event.target.closest && event.target.closest('summary.mobile-facets__open-wrapper');
      if (summary && document.activeElement !== summary) summary.focus({ preventScroll: true });
    },
    true
  );

  document.addEventListener('click', function (event) {
    const summary = event.target.closest && event.target.closest('summary.mobile-facets__open-wrapper');
    if (!summary) return;
    const details = summary.parentNode;
    if (details.hasAttribute('open')) return; // this click closes it
    setTimeout(function () {
      const dialog = details.hasAttribute('open') && details.querySelector('.mobile-facets__inner');
      if (dialog) dialog.focus({ preventScroll: true });
    }, 0);
  });

  // "Show N items" inside an open filter closes the drawer from two levels down, and the theme
  // then hands focus to that filter's row, which is on its way out: focus fell to the page. Every
  // control that closes the drawer returns focus to the trigger, once the theme's handlers have run.
  document.addEventListener('click', function (event) {
    const closer =
      event.target.closest && event.target.closest('.mobile-facets__footer [data-af-apply-label], .mobile-facets__close-drawer');
    if (!closer) return;
    const wrapper = closer.closest('.mobile-facets__wrapper');
    const trigger = wrapper && wrapper.querySelector('summary.mobile-facets__open-wrapper');
    if (trigger) setTimeout(function () { trigger.focus({ preventScroll: true }); }, 0);
  });

  document.addEventListener(
    'keydown',
    function (event) {
      if (event.key !== 'Tab') return;
      const details = openDrawer();
      const dialog = details && details.querySelector('.mobile-facets__inner');
      if (!dialog) return;
      const stops = getFocusableElements(dialog).filter(function (element) {
        return (
          element.tabIndex >= 0 &&
          element.getClientRects().length > 0 &&
          getComputedStyle(element).visibility !== 'hidden'
        );
      });
      if (!stops.length) return;
      const first = stops[0];
      const last = stops[stops.length - 1];
      const active = document.activeElement;
      let target = null;
      if (!dialog.contains(active)) target = event.shiftKey ? last : first;
      else if (event.shiftKey && (active === first || active === dialog)) target = last;
      else if (!event.shiftKey && active === last) target = first;
      if (!target) return;
      event.preventDefault();
      event.stopPropagation();
      target.focus();
    },
    true
  );
})();
